'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PenTool, Layers, Box, Eye, Upload, Play, Trash2, Download,
    Palette, Move, RotateCw, Copy, Type, Grid, Ruler, Zap,
    Monitor, Cpu, FileText, ChevronRight, CheckCircle, AlertCircle,
    Terminal, Camera, Brush, RefreshCw, FolderOpen, HardDrive,
    Server, Database, ArrowRight, ChevronDown, File, Search,
    Settings, Activity, MessageSquare, Send, BookOpen, Sparkles,
    ExternalLink, Building2, Link, Component, X} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';

import { CadViewer } from '@/components/CadViewer';

// ═══════════════════════════════════════
// Types
// ═══════════════════════════════════════
interface NASProject {
    name: string;
    path: string;
    modified: string;
}

interface DWGFile {
    name: string;
    size: number;
    modified: string;
    path: string;
}

interface LogEntry {
    id: string;
    type: 'info' | 'success' | 'error' | 'command' | 'pipeline';
    message: string;
    timestamp: Date;
}

interface AnalysisResult {
    version: string;
    project: { id: string; name: string; file: string };
    fileStatus: { exists: boolean; size: number; sizeFormatted: string; format: string };
    parsed?: {
        summary: Record<string, number>;
        quantities: Array<{ roomTag: string; specCode: string; category: string; quantity: number; unit: string; basis: string; layerName: string }>;
        warnings: string[];
    } | null;
    extracted?: { layers: string[]; roomNames: string[]; layerCount: number; textCount: number } | null;
    aiAnalysis?: string | null;
    rooms: Array<{ name: string; area: number; unit: string; finishLayers: number }>;
    analysisStatus: string;
    pipeline: { source: string; analyzer: string; output?: string };
    meta: { analyzedAt: string; durationMs: number; signedBy: string; hash?: string };
}

interface RAGMessage {
    id: string;
    role: 'user' | 'bot';
    content: string;
    sources?: { file: string; context: string }[];
    timestamp: Date;
}

// ═══════════════════════════════════════
// BTL Project Folder Icons
// ═══════════════════════════════════════
const FOLDER_ICONS: Record<string, string> = {
 '03_DWG': '', '04_설계견적서': '', '05_PPT': '',
 '06_IMAGE': '', '11_SKP': '', '12_개략공사비': '',
 '13_내역서': '', '18_에이전트': '', '01_받은자료': '',
};

const CAD_COLORS: Record<string, string> = {
    'Red': '#FF0000', 'Yellow': '#FFFF00', 'Green': '#00FF00',
    'Cyan': '#00FFFF', 'Blue': '#0000FF', 'Magenta': '#FF00FF',
    'White': '#FFFFFF', 'Gray': '#808080',
};

// ═══════════════════════════════════════
// Main Page Component
// ═══════════════════════════════════════
export default function CADAgentPage() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Pipeline state
    const [projects, setProjects] = useState<NASProject[]>([]);
    const [selectedProject, setSelectedProject] = useState<NASProject | null>(null);
    const [dwgFiles, setDWGFiles] = useState<DWGFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<DWGFile | null>(null);
    const [openTabs, setOpenTabs] = useState<DWGFile[]>([]);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(-1);
    const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
    const [nasSource, setNasSource] = useState<'nas' | 'simulated'>('simulated');

    // UI state
    const [logs, setLogs] = useState<LogEntry[]>([
 { id: '0', type: 'info', message: ' CAD Agent v2.0 — NAS 기반 파이프라인 준비', timestamp: new Date() }
    ]);
    const [pipelineStage, setPipelineStage] = useState<0 | 1 | 2 | 3 | 4>(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cadTarget, setCadTarget] = useState<string>('autocad');
    const [nlpInput, setNlpInput] = useState('');
    const [activePanel, setActivePanel] = useState<'pipeline' | 'draw' | 'nlp'>('pipeline');

    // RAG state
    const [ragMessages, setRagMessages] = useState<RAGMessage[]>([]);
    const [ragInput, setRagInput] = useState('');
    const [ragLoading, setRagLoading] = useState(false);
    const [ragSessionId, setRagSessionId] = useState<string | null>(null);
    const [rightTab, setRightTab] = useState<'logs' | 'rag' | 'arch' | 'batch'>('logs');

    // NAS Browser state
    const [leftTab, setLeftTab] = useState<'projects' | 'browse' | 'search'>('projects');
    const [browseItems, setBrowseItems] = useState<any[]>([]);
    const [browsePath, setBrowsePath] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [nodeHealth, setNodeHealth] = useState<any>(null);

    // Batch analysis state
    const [batchResults, setBatchResults] = useState<any>(null);
    const [batchLoading, setBatchLoading] = useState(false);
    const [layerValidation, setLayerValidation] = useState<any>(null);

    // Canvas
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const addLog = useCallback((type: LogEntry['type'], message: string) => {
        setLogs(prev => [...prev, { id: Date.now().toString(), type, message, timestamp: new Date() }]);
    }, []);

    // NAS Browse
    const browseNas = useCallback(async (dir: string) => {
        setIsProcessing(true);
        try {
            const res = await fetch(`/api/cad-agent?action=browse&dir=${encodeURIComponent(dir)}`);
            const data = await res.json();
            setBrowseItems(data.items || []);
            setBrowsePath(dir);
 addLog('command', ` ${dir || '/'} → ${(data.items || []).length}개 항목`);
 } catch { addLog('error', ' NAS 탐색 실패'); }
        setIsProcessing(false);
    }, [addLog]);

    // NAS Search
    const searchNas = useCallback(async () => {
        if (!searchQuery.trim()) return;
        setSearchLoading(true);
        try {
            const res = await fetch(`/api/cad-agent?action=search&q=${encodeURIComponent(searchQuery)}`);
            const data = await res.json();
            setSearchResults(data.results || []);
 addLog('success', ` "${searchQuery}" → ${data.total || 0}건`);
 } catch { addLog('error', ' 검색 실패'); }
        setSearchLoading(false);
    }, [searchQuery, addLog]);

    // Node Health
    const checkNodes = useCallback(async () => {
        try {
            const res = await fetch('https://grid.snapcodex.com/api/nodes-health');
            const data = await res.json();
            setNodeHealth(data);
        } catch {}
    }, []);
    useEffect(() => { checkNodes(); const t = setInterval(checkNodes, 60000); return () => clearInterval(t); }, [checkNodes]);

    // Export analysis JSON
    const exportAnalysis = useCallback(() => {
        if (!analysisResult) return;
        const blob = new Blob([JSON.stringify(analysisResult, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cxd_${analysisResult.project.name}_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
 addLog('success', ' CXD 분석 결과 JSON 내보내기 완료');
    }, [analysisResult, addLog]);

    // ═══════════════════════════════════════
    // Pipeline Stage 1: Load Projects from NAS
    // ═══════════════════════════════════════
    const loadProjects = useCallback(async () => {
        setIsProcessing(true);
 addLog('pipeline', ' NAS 연결 중... (Poby → grid.snapcodex.com → SSH 124.55.60.40)');

        try {
            const res = await fetch('/api/cad-agent?action=projects');
            const data = await res.json();

            if (data.source === 'error') {
 addLog('error', ` ${data.error}`);
 if (data.hint) addLog('info', ` ${data.hint}`);
                setIsProcessing(false);
                return;
            }

            if (data.source === 'nas-empty') {
 addLog('error', ` ${data.message}`);
                if (data.rootFolders?.length > 0) {
 addLog('info', ` NAS 루트 폴더: ${data.rootFolders.join(', ')}`);
                }
                setNasSource('simulated');
                setPipelineStage(0);
                setIsProcessing(false);
                return;
            }

            setProjects(data.projects || []);
            setNasSource(data.source === 'nas' ? 'nas' : 'simulated');
            setPipelineStage(1);

            if (data.rootFolders?.length > 0) {
 addLog('info', ` NAS 루트: ${data.rootFolders.join(', ')}`);
            }
 addLog('success', ` ${data.projects?.length || 0}개 프로젝트 로드 (${data.source === 'nas' ? ' NAS 직접' : ' 시뮬레이션'}) — ${data.nasServer || 'Poby'}`);
        } catch (err: any) {
 addLog('error', ` NAS 연결 실패: ${err.message}`);
        }
        setIsProcessing(false);
    }, [addLog]);

    // ═══════════════════════════════════════
    // Pipeline Stage 2: Scan DWG files
    // ═══════════════════════════════════════
    const selectProject = useCallback(async (project: NASProject) => {
        setSelectedProject(project);
        setIsProcessing(true);
 addLog('pipeline', ` 프로젝트 선택: ${project.name}`);
 addLog('command', ` ${project.path}/03_DWG 스캔 중...`);

        try {
            const res = await fetch(`/api/cad-agent?action=dwg-list&project=${encodeURIComponent(project.name)}`);
            const data = await res.json();
            setDWGFiles(data.files || []);
            setPipelineStage(2);
 addLog('success', ` ${data.files?.length || 0}개 DWG/DXF 파일 발견`);
        } catch {
 addLog('error', ' DWG 파일 스캔 실패');
        }
        setIsProcessing(false);
    }, [addLog]);

    // ═══════════════════════════════════════
    // Pipeline Stage 3: Analyze DWG
    // ═══════════════════════════════════════
    const analyzeFile = useCallback(async (file: DWGFile) => {
        setSelectedFile(file);
        setIsProcessing(true);
        setPipelineStage(3);

 addLog('pipeline', ` CAD 분석 시작: ${file.name}`);
        addLog('command', '  [1/5] DWG 파일 로드 (ezdxf)...');
        await new Promise(r => setTimeout(r, 400));
        addLog('command', '  [2/5] 엔티티 추출 (LINE, CIRCLE, HATCH...)...');
        await new Promise(r => setTimeout(r, 500));
        addLog('command', '  [3/5] 레이어 분석 (A-WALL, A-DOOR, A-GLAZ...)...');
        await new Promise(r => setTimeout(r, 400));
        addLog('command', '  [4/5] 실 공간 탐지 (HATCH → Room Detection)...');
        await new Promise(r => setTimeout(r, 600));
        addLog('command', '  [5/5] LLM 건축 분석 요약 생성...');
        await new Promise(r => setTimeout(r, 300));

        try {
            const res = await fetch('/api/cad-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'analyze',
                    projectId: selectedProject?.name,
                    filePath: file.path,
                    fileName: file.name,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setAnalysisResult(data.result);
                setPipelineStage(4);
 addLog('success', ` ${data.message}`);
 addLog('pipeline', ` CXD 저장 → ${data.result.pipeline.output}`);
 addLog('info', ` 예상 공사비: ${data.result.estimatedCost}`);
                drawAnalysis(data.result);
            }
        } catch {
 addLog('error', ' 분석 실패');
        }
        setIsProcessing(false);
    }, [selectedProject, addLog]);

    // ── 배치 분석: 전체 DWG 일괄 처리 ──
    const batchAnalyze = useCallback(async () => {
        if (dwgFiles.length === 0) return;
        setBatchLoading(true);
        setBatchResults(null);
        addLog('pipeline', ` 배치 분석 시작: ${dwgFiles.length}개 파일`);

        try {
            const res = await fetch('/api/cad-agent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'batch-analyze',
                    projectId: selectedProject?.name,
                    files: dwgFiles.map(f => f.path),
                    wallHeight: 2.7,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setBatchResults(data);
                addLog('success', ` 배치 분석 완료: ${data.successCount}/${data.totalFiles} 성공, ${data.totalQuantities}개 수량 항목`);

                // 레이어 검증도 함께 실행
                const allLayers = [...new Set(data.results.flatMap((r: any) => r.layers || []))];
                if (allLayers.length > 0) {
                    const valRes = await fetch('/api/cad-agent', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'validate-layers', layers: allLayers }),
                    });
                    const valData = await valRes.json();
                    if (valData.success) {
                        setLayerValidation(valData);
                        addLog(valData.compliant ? 'success' : 'info', ` 레이어 검증: ${valData.score}점 (${valData.passCount}통과, ${valData.failCount}실패, ${valData.warnCount}경고)`);
                    }
                }
            }
        } catch (e: any) {
            addLog('error', ` 배치 분석 실패: ${e.message}`);
        }
        setBatchLoading(false);
    }, [dwgFiles, selectedProject, addLog]);

    // ═══════════════════════════════════════
    // Canvas: Draw Analysis Visualization
    // ═══════════════════════════════════════
    const drawAnalysis = useCallback((result: AnalysisResult) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const W = canvas.width, H = canvas.height;

        ctx.fillStyle = '#080810';
        ctx.fillRect(0, 0, W, H);

        // Grid
        ctx.strokeStyle = '#12121e';
        for (let x = 0; x < W; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
        for (let y = 0; y < H; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

        // Draw rooms
        const rooms = result.rooms || [];
        const colors = ['#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
        let offsetX = 60;
        rooms.forEach((room, i) => {
            const rW = Math.sqrt(room.area || 10) * 12;
            const rH = Math.sqrt(room.area || 10) * 10;
            const y = 80 + (i % 2) * (rH + 30);

            ctx.strokeStyle = colors[i % colors.length];
            ctx.lineWidth = 2;
            ctx.strokeRect(offsetX, y, rW, rH);

            // Fill with transparency
            ctx.fillStyle = colors[i % colors.length] + '15';
            ctx.fillRect(offsetX, y, rW, rH);

            // Room label
            ctx.fillStyle = colors[i % colors.length];
            ctx.font = 'bold 11px Inter, sans-serif';
            ctx.fillText(room.name, offsetX + 6, y + 16);
            ctx.font = '9px Inter, sans-serif';
            ctx.fillStyle = '#666';
            ctx.fillText(`${room.area}㎡`, offsetX + 6, y + 30);
            ctx.fillText(`마감 ${room.finishLayers}건`, offsetX + 6, y + 42);

            // Door markers
            ctx.fillStyle = '#22c55e';
            ctx.fillRect(offsetX + rW / 2 - 8, y + rH - 2, 16, 4);

            offsetX += rW + 20;
        });

        // Title
        ctx.fillStyle = '#10b981';
        ctx.font = 'bold 14px Inter, sans-serif';
 ctx.fillText(` ${result.project.name} — ${result.project.file}`, 15, 30);
        ctx.font = '11px Inter, sans-serif';
        ctx.fillStyle = '#555';
        ctx.fillText(`${result.extracted?.textCount || 0} entities · ${rooms.length} rooms`, 15, 48);

        // Layer legend
        const legendY = H - 60;
        ctx.fillStyle = '#333';
        ctx.fillRect(10, legendY - 5, W - 20, 55);
        ctx.fillStyle = '#888';
        ctx.font = '9px Inter, sans-serif';
        (result.extracted?.layers || []).slice(0, 5).forEach((layerName: string, i: number) => {
            const lx = 20 + i * 120;
            const acColors = ['#FF0000', '#FFFF00', '#00FF00', '#00FFFF', '#0000FF', '#FF00FF'];
            ctx.fillStyle = acColors[i % acColors.length];
            ctx.fillRect(lx, legendY + 5, 8, 8);
            ctx.fillStyle = '#999';
            ctx.fillText(`${layerName}`, lx + 12, legendY + 13);
        });
    }, []);

    // ═══════════════════════════════════════
    // NLP Processing
    // ═══════════════════════════════════════
    const processNLP = useCallback(async () => {
        if (!nlpInput.trim()) return;
        setIsProcessing(true);
 addLog('command', ` "${nlpInput}"`);
        const text = nlpInput.toLowerCase();
        await new Promise(r => setTimeout(r, 500));

        if (text.includes('프로젝트') || text.includes('목록')) {
            await loadProjects();
        } else if (text.includes('분석') && selectedFile) {
            await analyzeFile(selectedFile);
        } else if (text.includes('웅촌') || text.includes('도서관') || text.includes('의령')) {
            const match = projects.find(p => text.split('').some(c => p.name.includes(c)));
            if (match) await selectProject(match);
 else addLog('info', ' 프로젝트를 찾지 못했습니다. 먼저 NAS에서 프로젝트를 로드하세요.');
        } else {
 addLog('info', ` AI: "${nlpInput}" → 명령 분석 중...`);
        }

        setNlpInput('');
        setIsProcessing(false);
    }, [nlpInput, selectedFile, projects, addLog, loadProjects, analyzeFile, selectProject]);

    // ═══════════════════════════════════════
    // RAG Explorer
    // ═══════════════════════════════════════
    const sendRAG = useCallback(async () => {
        if (!ragInput.trim()) return;
        setRagLoading(true);
        const userMsg: RAGMessage = { id: Date.now().toString(), role: 'user', content: ragInput, timestamp: new Date() };
        setRagMessages(prev => [...prev, userMsg]);
        const query = ragInput;
        setRagInput('');

        // Build CAD context
        const cadContext = [
            selectedProject ? `현재 프로젝트: ${selectedProject.name}` : '',
            selectedFile ? `선택된 도면: ${selectedFile.name}` : '',
            analysisResult ? `분석 결과: ${analysisResult.extracted?.textCount || 0}개 엔티티, ${analysisResult.rooms.length}개 실` : '',
        ].filter(Boolean).join('\n');

        try {
            const apiMessages = [{ role: 'user', content: cadContext ? `[CAD Agent 컨텍스트]\n${cadContext}\n\n질문: ${query}` : query }];

            const res = await fetch('/api/omni-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: apiMessages,
                    metadata: { sessionId: ragSessionId, context: 'cad-agent' },
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            if (data.sessionId) setRagSessionId(data.sessionId);
            
            const fullText = data.answer || data.content || 'Poby Gateway에 연결할 수 없습니다.';
            const botMsgId = (Date.now() + 1).toString();
            
            setRagMessages(prev => [...prev, { id: botMsgId, role: 'bot', content: fullText, timestamp: new Date() }]);

 addLog('info', ` RAG 탐색: "${query.slice(0, 30)}..."`);
        } catch (err: any) {
            const errMsg = `Poby 연결 실패: ${err.message}`;
 setRagMessages(prev =>[...prev, { id: (Date.now() + 2).toString(), role: 'bot', content: ` ${errMsg}`, timestamp: new Date() }]);
 addLog('error', ` RAG: ${errMsg}`);
        }
        setRagLoading(false);
    }, [ragInput, ragSessionId, selectedProject, selectedFile, analysisResult, addLog]);

    // Auto-scroll logs
    useEffect(() => {
        const el = document.getElementById('cad-log-container');
        if (el) el.scrollTop = el.scrollHeight;
    }, [logs]);

    // Auto-scroll RAG
    useEffect(() => {
        const el = document.getElementById('rag-container');
        if (el) el.scrollTop = el.scrollHeight;
    }, [ragMessages]);

    // Init canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#080810';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#12121e';
        for (let x = 0; x < canvas.width; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke(); }
        for (let y = 0; y < canvas.height; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke(); }
        ctx.fillStyle = '#333';
        ctx.font = '13px Inter, sans-serif';
        ctx.fillText('NAS에서 프로젝트를 선택하고 DWG를 분석하면 여기에 결과가 표시됩니다', 100, 250);
    }, []);

    // ═══════════════════════════════════════
    // Pipeline Stage Indicator
    // ═══════════════════════════════════════
    const stages = [
        { label: 'NAS 연결', icon: <Server className="w-3.5 h-3.5" />, desc: 'Poby WebDAV' },
        { label: '프로젝트 선택', icon: <FolderOpen className="w-3.5 h-3.5" />, desc: 'B_DESIGN' },
        { label: 'DWG 스캔', icon: <Search className="w-3.5 h-3.5" />, desc: '03_DWG' },
        { label: 'CAD 분석', icon: <Cpu className="w-3.5 h-3.5" />, desc: 'MCP + ezdxf' },
        { label: 'CXD 저장', icon: <Database className="w-3.5 h-3.5" />, desc: '18_에이전트' },
    ];

    return (
        <>
            <main className={`min-h-screen pt-16 ${isDark ? 'bg-gray-950 text-white' : 'bg-slate-50 text-gray-900'}`}>

                {/* Hero */}
                <section className={`relative overflow-hidden border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-cyan-500/5 to-blue-600/10" />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 relative z-10">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-white text-lg shrink-0"><PenTool className="w-5 h-5" /></div>
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold">CAD Agent</h1>
                                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        NAS → DWG 분석 → CXD 수량표 → 공사비 예측 파이프라인
                                    </p>
                                </div>
                            </div>
                            {/* CAD Target Selector */}
                            <div className="flex flex-wrap gap-1.5">
                                {['autocad', 'zwcad', 'gstarcad', 'bricscad', 'freecad'].map(cad => (
                                    <button key={cad} onClick={() => setCadTarget(cad)}
                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition cursor-pointer ${cadTarget === cad
                                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                            : isDark ? 'bg-white/5 text-gray-500 border border-white/10' : 'bg-white text-gray-400 border border-gray-200'}`}>
                                        {cad.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Pipeline Progress */}
                        <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-1">
                            {stages.map((s, i) => (
                                <div key={i} className="flex items-center shrink-0">
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${
                                        i < pipelineStage
                                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                            : i === pipelineStage
                                                ? isProcessing
                                                    ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 animate-pulse'
                                                    : isDark ? 'bg-white/10 text-white border border-white/20' : 'bg-white text-gray-700 border border-gray-300'
                                                : isDark ? 'bg-white/[0.03] text-gray-600 border border-white/5' : 'bg-gray-100 text-gray-400 border border-gray-200'
                                    }`}>
                                        {i < pipelineStage ? <CheckCircle className="w-3 h-3" /> : s.icon}
                                        <span className="hidden sm:inline">{s.label}</span>
                                    </div>
                                    {i < stages.length - 1 && <ChevronRight className={`w-3 h-3 mx-0.5 ${i < pipelineStage ? 'text-emerald-500' : isDark ? 'text-gray-700' : 'text-gray-300'}`} />}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Main Content */}
                <div className="w-full px-4 sm:px-6 py-5">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

                        {/* Left: NAS Panel */}
                        <div className="lg:col-span-3 space-y-3">
                            {/* Node Status Mini */}
                            {nodeHealth && (
                                <div className={`rounded-xl border p-2.5 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-center justify-between mb-1.5">
 <span className="text-[10px] font-bold"><Monitor className="w-4 h-4 inline-block" />SnapGRID Nodes</span>
                                        <span className={`text-[10px] font-bold ${(nodeHealth.nodes?.filter((n:any)=>n.online).length||0) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {nodeHealth.nodes?.filter((n:any)=>n.online).length||0}/{nodeHealth.nodes?.length||0}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {(nodeHealth.nodes||[]).slice(0,9).map((n:any) => (
                                            <div key={n.id} title={`${n.name} — ${n.online ? n.latency+'ms' : 'offline'}`}
                                                className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] ${n.online ? isDark ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200' : isDark ? 'bg-red-500/10 border border-red-500/20 opacity-40' : 'bg-gray-100 border border-gray-200 opacity-40'}`}>
                                                {n.id?.[0]?.toUpperCase()}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Main NAS Panel */}
                            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                {/* Tab Selector */}
                                <div className={`px-2 py-1.5 border-b flex gap-0.5 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                    {[
                                        { id: 'projects' as const, label: '프로젝트', icon: <FolderOpen className="w-3 h-3" /> },
                                        { id: 'browse' as const, label: '탐색', icon: <HardDrive className="w-3 h-3" /> },
                                        { id: 'search' as const, label: '검색', icon: <Search className="w-3 h-3" /> },
                                    ].map(t => (
                                        <button key={t.id} onClick={() => { setLeftTab(t.id); if (t.id === 'browse' && browseItems.length === 0) browseNas(''); }}
                                            className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-lg text-[10px] font-bold transition cursor-pointer ${
                                                leftTab === t.id
                                                    ? isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                                                    : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                                            }`}>
                                            {t.icon}{t.label}
                                        </button>
                                    ))}
                                    <span className={`flex items-center text-[8px] px-1.5 rounded-lg ${nasSource === 'nas' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
 {nasSource === 'nas' ? '' : ''}
                                    </span>
                                </div>

                                {/* === PROJECTS TAB === */}
                                {leftTab === 'projects' && (
                                    <>
                                        {pipelineStage === 0 && (
                                            <div className="p-4">
                                                <button onClick={loadProjects} disabled={isProcessing}
                                                    className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white text-xs font-bold hover:opacity-90 disabled:opacity-40 transition cursor-pointer flex items-center justify-center gap-2">
                                                    {isProcessing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Server className="w-4 h-4" />}
                                                    NAS 연결 (Poby)
                                                </button>
                                                <p className={`text-[10px] mt-2 text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                    grid.snapcodex.com → Synology NAS
                                                </p>
                                            </div>
                                        )}
                                        {pipelineStage >= 1 && (
                                            <div className="p-2 max-h-[280px] overflow-y-auto space-y-1">
                                                <button onClick={loadProjects} className={`w-full text-center py-1.5 rounded-lg text-[10px] transition cursor-pointer mb-1 ${isDark ? 'text-gray-600 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-50'}`}>
                                                    <RefreshCw className="w-3 h-3 inline mr-1" />새로고침
                                                </button>
                                                {projects.map(p => (
                                                    <button key={p.name} onClick={() => selectProject(p)}
                                                        className={`w-full text-left px-3 py-2 rounded-xl text-[11px] transition cursor-pointer ${
                                                            selectedProject?.name === p.name
                                                                ? isDark ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-300'
                                                                : isDark ? 'hover:bg-white/5 border border-transparent' : 'hover:bg-gray-50 border border-transparent'
                                                        }`}>
                                                        <div className="font-bold truncate">{p.name}</div>
                                                        <div className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{p.modified}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {pipelineStage >= 2 && dwgFiles.length > 0 && (
                                            <>
                                                <div className={`px-4 py-2 border-t border-b text-[10px] font-bold flex items-center justify-between gap-1.5 ${isDark ? 'border-white/10 bg-white/[0.02] text-emerald-400' : 'border-gray-100 bg-gray-50 text-emerald-600'}`}>
                                                    <span className="flex items-center gap-1.5"><File className="w-3 h-3" /> 03_DWG ({dwgFiles.length})</span>
                                                    <button onClick={batchAnalyze} disabled={batchLoading || dwgFiles.length === 0}
                                                        className={`px-2 py-0.5 rounded text-[9px] font-bold transition ${batchLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:scale-105'} bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30`}>
                                                        {batchLoading ? (
                                                            <span className="flex items-center gap-1"><RefreshCw className="w-2.5 h-2.5 animate-spin" />분석 중...</span>
                                                        ) : (
                                                            <span className="flex items-center gap-1"><Zap className="w-2.5 h-2.5" />전체 일괄 분석</span>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="p-2 space-y-1">
                                                    {dwgFiles.map(f => (
                                                        <div key={f.name} className={`flex items-center justify-between px-3 py-2 rounded-lg transition ${
                                                            selectedFile?.name === f.name
                                                                ? isDark ? 'bg-cyan-500/15 border border-cyan-500/30' : 'bg-cyan-50 border border-cyan-300'
                                                                : isDark ? 'hover:bg-white/5 border border-transparent' : 'hover:bg-gray-50 border border-transparent'
                                                        }`}>
                                                            <button onClick={() => analyzeFile(f)} className="flex items-center gap-2 min-w-0 flex-1 text-left cursor-pointer">
 <span className="text-lg"><Ruler className="w-4 h-4 inline-block" /></span>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="font-semibold text-[10px] truncate">{f.name}</div>
                                                                    <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                                        {(f.size / 1024 / 1024).toFixed(1)} MB · {f.modified}
                                                                    </div>
                                                                </div>
                                                            </button>
                                                            <div className="flex gap-1 shrink-0">
                                                                <button onClick={() => { setOpenTabs(prev => prev.some(t => t.path === f.path) ? prev : [...prev, f]); setActiveTabIndex(prev => prev < 0 ? 0 : prev); }} title="웹 뷰어로 보기" className={`p-1.5 rounded-md cursor-pointer ${isDark ? 'hover:bg-white/10 text-emerald-400' : 'hover:bg-emerald-100 text-emerald-600'}`}>
                                                                    <Eye className="w-3.5 h-3.5" />
                                                                </button>
                                                                <button onClick={() => analyzeFile(f)} title="분석 실행" className={`p-1.5 rounded-md cursor-pointer ${isDark ? 'hover:bg-white/10 text-cyan-400' : 'hover:bg-cyan-100 text-cyan-600'}`}>
                                                                    <Play className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {/* === BROWSE TAB === */}
                                {leftTab === 'browse' && (
                                    <div className="p-2">
                                        {/* Breadcrumb */}
                                        <div className={`flex items-center gap-1 text-[10px] mb-2 px-2 py-1.5 rounded-lg overflow-x-auto ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                                            <button onClick={() => browseNas('')} className="text-emerald-400 hover:underline cursor-pointer shrink-0">NAS</button>
                                            {browsePath.split('/').filter(Boolean).map((seg, i, arr) => (
                                                <span key={i} className="flex items-center gap-1 shrink-0">
                                                    <ChevronRight className="w-2.5 h-2.5 text-gray-600" />
                                                    <button onClick={() => browseNas(arr.slice(0, i + 1).join('/'))}
                                                        className="text-emerald-400 hover:underline cursor-pointer truncate max-w-[80px]">{seg}</button>
                                                </span>
                                            ))}
                                        </div>
                                        <div className="max-h-[350px] overflow-y-auto space-y-0.5">
                                            {browseItems.length === 0 && !isProcessing && (
                                                <div className={`text-center py-8 text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
 {nasSource === 'nas' ? '빈 디렉토리' : ' NAS 마운트 필요'}
                                                </div>
                                            )}
                                            {browseItems.map((item: any, i: number) => {
                                                const isCad = item.type !== 'dir' && /\.(dwg|dxf|dxf2)$/i.test(item.name || '');
                                                const filePath = item.path || `${browsePath}/${item.name}`;
                                                return (
                                                <div key={i} className={`flex items-center gap-1 rounded-lg transition ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                                                    <button onClick={() => {
                                                        if (item.type === 'dir') { browseNas(filePath); }
 else if (isCad) { const nf = { name: item.name, size: item.size || 0, modified: '', path: filePath }; setOpenTabs(prev => prev.some(t => t.path === nf.path) ? prev : [...prev, nf]); setActiveTabIndex(prev => prev < 0 ? 0 : prev); addLog('info', ` CAD 뷰어: ${item.name}`); }
 else { addLog('info', ` ${item.name} (${((item.size||0)/1024).toFixed(0)} KB)`); }
                                                    }}
                                                        className="flex-1 text-left px-2.5 py-1.5 text-[10px] cursor-pointer flex items-center gap-2 min-w-0">
 <span>{item.type === 'dir' ? '' : isCad ? '' : ''}</span>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="truncate font-medium">{item.name || item.filename}</div>
                                                        </div>
                                                        {item.type !== 'dir' && item.size && (
                                                            <span className={`text-[8px] shrink-0 ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>
                                                                {item.size > 1048576 ? `${(item.size/1048576).toFixed(1)}M` : `${(item.size/1024).toFixed(0)}K`}
                                                            </span>
                                                        )}
                                                    </button>
                                                    {isCad && (
                                                        <button onClick={() => { const nf = { name: item.name, size: item.size || 0, modified: '', path: filePath }; setOpenTabs(prev => prev.some(t => t.path === nf.path) ? prev : [...prev, nf]); setActiveTabIndex(prev => prev < 0 ? 0 : prev); }}
                                                            className={`p-1 rounded-md shrink-0 mr-1 ${isDark ? 'hover:bg-white/10 text-emerald-400' : 'hover:bg-emerald-100 text-emerald-600'}`} title="CAD 뷰어">
                                                            <Eye className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* === SEARCH TAB === */}
                                {leftTab === 'search' && (
                                    <div className="p-2">
                                        <div className="flex gap-1.5 mb-2">
                                            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && searchNas()}
                                                placeholder="DWG, 도서관, 견적..."
                                                className={`flex-1 px-2.5 py-1.5 rounded-lg text-[10px] border outline-none ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-600' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'}`}
                                            />
                                            <button onClick={searchNas} disabled={searchLoading}
                                                className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[10px] font-bold cursor-pointer disabled:opacity-40">
                                                {searchLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                                            </button>
                                        </div>
                                        <div className="max-h-[350px] overflow-y-auto space-y-1">
                                            {searchResults.length === 0 && !searchLoading && (
                                                <div className={`text-center py-6 text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                    NAS 27k+ 파일에서 검색
                                                </div>
                                            )}
                                            {searchResults.map((r: any, i: number) => (
                                                <div key={i} className={`px-2.5 py-2 rounded-lg text-[10px] ${isDark ? 'bg-white/[0.03] hover:bg-white/[0.06]' : 'bg-gray-50 hover:bg-gray-100'} transition cursor-pointer`}
 onClick={() =>addLog('info', ` ${r.path}`)}>
                                                    <div className="font-semibold truncate">{r.title || r.name}</div>
                                                    <div className={`text-[8px] truncate mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{r.path}</div>
                                                    {r.score && <div className="text-[8px] text-emerald-400 mt-0.5">관련도: {(r.score * 100).toFixed(0)}%</div>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Center: Canvas + Results */}
                        <div className="lg:col-span-6">
                            {/* Canvas */}
                            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                    <div className="flex items-center gap-2">
                                        <Grid className="w-4 h-4 text-emerald-400" />
                                        <span className="text-xs font-bold">CAD 뷰포트</span>
                                    </div>
                                    <div className="flex gap-1">
 <button onClick={() =>addLog('success', ' 스크린샷 캡처')}
                                            className={`p-1.5 rounded-lg cursor-pointer ${isDark ? 'hover:bg-white/10 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}>
                                            <Camera className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={exportAnalysis} title="CXD JSON 내보내기"
                                            className={`p-1.5 rounded-lg cursor-pointer ${isDark ? 'hover:bg-white/10 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}>
                                            <Download className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                                <canvas ref={canvasRef} width={1000} height={480} className="w-full bg-[#080810]" style={{ imageRendering: 'crisp-edges' }} />
                            </div>

                            {/* Analysis Result Cards */}
                            {analysisResult && (
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 grid grid-cols-2 gap-3">
                                    {/* Rooms */}
                                    <div className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                        <h4 className="text-[10px] font-bold text-emerald-400 mb-2 flex items-center gap-1"><Box className="w-3 h-3" /> 실 공간 ({analysisResult.rooms.length})</h4>
                                        <div className="space-y-1.5">
                                            {analysisResult.rooms.map((r, i) => (
                                                <div key={i} className={`flex justify-between items-center px-2 py-1.5 rounded-lg text-[10px] ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                                                    <span className="font-semibold">{r.name}</span>
                                                    <div className="flex gap-2">
                                                        <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{r.area}㎡</span>
                                                        <span className="text-emerald-400">레이어 {r.finishLayers}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Summary */}
                                    <div className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                        <h4 className="text-[10px] font-bold text-cyan-400 mb-2 flex items-center gap-1"><Activity className="w-3 h-3" /> 분석 요약</h4>
                                        <div className="space-y-1.5 text-[10px]">
                                            <div className="flex justify-between"><span className={isDark ? 'text-gray-500' : 'text-gray-400'}>산출 건수</span><span className="font-bold">{analysisResult.parsed?.quantities.length || 0}</span></div>
                                            <div className="flex justify-between"><span className={isDark ? 'text-gray-500' : 'text-gray-400'}>총 면적</span><span className="font-bold">{analysisResult.rooms.reduce((a, r) => a + (r.area || 0), 0).toFixed(1)}㎡</span></div>
                                            <div className={`flex justify-between pt-1.5 mt-1.5 border-t font-bold text-emerald-400 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                                <span>파서 모드</span><span>{analysisResult.pipeline.analyzer}</span>
                                            </div>

                                            <div className="flex justify-between"><span className={isDark ? 'text-gray-500' : 'text-gray-400'}>분석 시간</span><span>{analysisResult.meta.durationMs}ms</span></div>
                                            <div className="flex justify-between"><span className={isDark ? 'text-gray-500' : 'text-gray-400'}>서명</span><span className="text-[8px] font-mono truncate ml-2">{analysisResult.meta.signedBy}</span></div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Detailed Analysis Tables (실무용) */}
                            {analysisResult && (
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-4 space-y-4">
                                    {/* Room Finish Schedule (실별 마감재 표) */}
                                    <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                        <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                            <div className="flex items-center gap-2">
                                                <Layers className="w-4 h-4 text-emerald-400" />
                                                <span className="text-xs font-bold">실별 마감재표 — Room Finish Schedule</span>
                                            </div>
                                            <button onClick={exportAnalysis} className={`text-[10px] px-2 py-1 rounded-lg cursor-pointer flex items-center gap-1 ${isDark ? 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}>
                                                <Download className="w-3 h-3" /> CXD 내보내기
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-[10px]">
                                                <thead>
                                                    <tr className={isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                                                        <th className={`text-left px-3 py-2 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>실명 (Room)</th>
                                                        <th className={`text-left px-3 py-2 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>마감 부위</th>
                                                        <th className={`text-left px-3 py-2 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>스펙 (Spec)</th>
                                                        <th className={`text-right px-3 py-2 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>산출 수량</th>
                                                        <th className={`text-left px-3 py-2 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>단위</th>
                                                        <th className={`text-left px-3 py-2 font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>산출 근거 (Layer/Block)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {analysisResult.parsed?.quantities.length ? analysisResult.parsed.quantities.map((q, i) => (
                                                        <tr key={i} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'} transition`}>
                                                            <td className="px-3 py-2 font-semibold text-emerald-400">{q.roomTag}</td>
                                                            <td className="px-3 py-2 text-cyan-400">{q.category === 'floor' ? '바닥' : q.category === 'wall' ? '벽체' : q.category === 'ceiling' ? '천장' : q.category === 'furniture' ? '가구' : q.category === 'lighting_point' ? '조명(EA)' : q.category === 'lighting_linear' ? '조명(m)' : '기타'}</td>
                                                            <td className="px-3 py-2">{q.specCode}</td>
                                                            <td className="px-3 py-2 text-right font-mono font-bold text-amber-400">{q.quantity.toLocaleString()}</td>
                                                            <td className="px-3 py-2">{q.unit}</td>
                                                            <td className={`px-3 py-2 truncate max-w-[200px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{q.basis}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr><td colSpan={6} className="text-center py-4 text-gray-500">DXF 파싱 결과가 없거나 수량 산출 데이터가 없습니다.</td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Entity & Layer Analysis */}
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Entity Distribution */}
                                        <div className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                            <h4 className="text-[10px] font-bold text-cyan-400 mb-2.5 flex items-center gap-1"><Activity className="w-3 h-3" /> 엔티티 분포</h4>
                                            <div className="space-y-1">
                                                {analysisResult.parsed ? Object.entries(analysisResult.parsed.summary)
                                                    .filter(([k]) => ['polylineCount', 'hatchCount', 'textCount', 'insertCount'].includes(k))
                                                    .map(([key, val]) => {
                                                    const max = Math.max(...Object.values(analysisResult.parsed!.summary) as number[]);
                                                    const pct = max > 0 ? (Number(val) / max) * 100 : 0;
                                                    return (
                                                        <div key={key} className="flex items-center gap-2 text-[10px]">
                                                            <span className={`w-20 font-mono shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{key.replace('Count', '')}</span>
                                                            <div className={`flex-1 h-3 rounded-sm overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                                                <div className="h-full rounded-sm bg-gradient-to-r from-cyan-500 to-emerald-500" style={{ width: `${pct}%` }} />
                                                            </div>
                                                            <span className="w-10 text-right font-mono font-bold">{val as React.ReactNode}</span>
                                                        </div>
                                                    );
                                                }) : <div className="text-[10px] text-gray-500">엔티티 요약 데이터가 없습니다.</div>}
                                            </div>
                                        </div>

                                        {/* Error / AI Analysis */}
                                        <div className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                            <h4 className="text-[10px] font-bold text-violet-400 mb-2.5 flex items-center gap-1"><Layers className="w-3 h-3" /> 경고 및 예외사항</h4>
                                            <div className="space-y-1.5 overflow-y-auto max-h-[80px]">
                                                {analysisResult.parsed?.warnings.length ? analysisResult.parsed.warnings.map((w, i) => (
                                                    <div key={i} className="flex items-start gap-1 text-[10px] text-amber-500">
                                                        <span>•</span>
                                                        <span className="leading-tight">{w}</span>
                                                    </div>
                                                )) : (
                                                    <div className="text-[10px] text-emerald-400">발견된 오류나 경고가 없습니다 (도면 품질 우수).</div>
                                                )}
                                            </div>

                                            <div className={`mt-3 pt-2 border-t text-[10px] flex justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>분석 시간</span>
                                                <span className="font-mono font-bold">{analysisResult.meta.durationMs}ms</span>
                                            </div>
                                            <div className="text-[10px] flex justify-between mt-1">
                                                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>서명</span>
                                                <span className="font-mono text-[8px] truncate ml-2">{analysisResult.meta.signedBy}</span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* NLP Input */}
                            <div className={`mt-4 rounded-xl border p-3 ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                <div className="flex gap-2">
                                    <input type="text" value={nlpInput} onChange={e => setNlpInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && processNLP()}
                                        placeholder='자연어 명령: "웅촌초 평면도 분석해줘"'
                                        className={`flex-1 px-3 py-2 rounded-lg text-xs border outline-none transition ${isDark
                                            ? 'bg-white/5 border-white/10 text-white placeholder-gray-600 focus:border-emerald-500/50'
                                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-400'}`}
                                    />
                                    <button onClick={processNLP} disabled={isProcessing || !nlpInput.trim()}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-600 text-white text-[10px] font-bold hover:opacity-90 disabled:opacity-40 transition cursor-pointer">
                                        {isProcessing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right: Tabs — Logs / RAG / Architecture */}
                        <div className="lg:col-span-3 space-y-0">
                            {/* Tab Selector */}
                            <div className={`flex gap-0.5 p-1 rounded-xl mb-3 ${isDark ? 'bg-gray-900/80' : 'bg-gray-100'}`}>
                                {[
                                    { id: 'logs' as const, label: '로그', icon: <Terminal className="w-3.5 h-3.5" /> },
                                    { id: 'rag' as const, label: 'RAG', icon: <BookOpen className="w-3.5 h-3.5" /> },
                                    { id: 'batch' as const, label: '배치', icon: <Zap className="w-3.5 h-3.5" /> },
                                    { id: 'arch' as const, label: '아키텍처', icon: <Server className="w-3.5 h-3.5" /> },
                                ].map(t => (
                                    <button key={t.id} onClick={() => setRightTab(t.id)}
                                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[10px] font-bold transition cursor-pointer ${
                                            rightTab === t.id
                                                ? isDark ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-white text-emerald-700 shadow-sm border border-emerald-200'
                                                : isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-white'
                                        }`}>
                                        {t.icon}{t.label}
                                        {t.id === 'rag' && ragMessages.length > 0 && (
                                            <span className={`text-[8px] px-1 rounded-full ${isDark ? 'bg-violet-500/30 text-violet-300' : 'bg-violet-100 text-violet-600'}`}>
                                                {ragMessages.length}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* === LOGS TAB === */}
                            {rightTab === 'logs' && (
                                <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                    <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        <div className="flex items-center gap-2">
                                            <Terminal className="w-4 h-4 text-emerald-400" />
                                            <span className="text-xs font-bold">파이프라인 로그</span>
                                        </div>
                                        <span className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{cadTarget.toUpperCase()}</span>
                                    </div>
                                    <div id="cad-log-container" className="h-[500px] overflow-y-auto p-2.5 space-y-1">
                                        {logs.map(log => (
                                            <motion.div key={log.id} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                                                className={`text-[10px] leading-relaxed px-2.5 py-1.5 rounded-lg ${
                                                    log.type === 'success' ? isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                                                    : log.type === 'error' ? isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-700'
                                                    : log.type === 'pipeline' ? isDark ? 'bg-violet-500/10 text-violet-300' : 'bg-violet-50 text-violet-700'
                                                    : log.type === 'command' ? isDark ? 'bg-cyan-500/10 text-cyan-300' : 'bg-cyan-50 text-cyan-700'
                                                    : isDark ? 'bg-white/[0.02] text-gray-500' : 'bg-gray-50 text-gray-500'
                                                }`}>
                                                <span className="opacity-40 text-[8px] mr-1">
                                                    {log.timestamp.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                </span>
                                                {log.message}
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* === RAG EXPLORER TAB === */}
                            {rightTab === 'rag' && (
                                <div className={`rounded-2xl border overflow-hidden flex flex-col ${isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200'}`}>
                                    <div className={`px-4 py-2.5 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        <div className="flex items-center gap-2">
                                            <BookOpen className="w-4 h-4 text-violet-400" />
                                            <span className="text-xs font-bold">RAG 탐색기</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {ragSessionId && (
                                                <span className={`text-[8px] px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-50 text-violet-600'}`}>
                                                    Session
                                                </span>
                                            )}
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-600'}`}>
                                                Poby RAG
                                            </span>
                                        </div>
                                    </div>

                                    {/* Messages */}
                                    <div id="rag-container" className="h-[400px] overflow-y-auto p-3 space-y-3">
                                        {ragMessages.length === 0 && (
                                            <div className="flex flex-col items-center justify-center h-full gap-3">
                                                <Sparkles className={`w-8 h-8 ${isDark ? 'text-violet-500/40' : 'text-violet-300'}`} />
                                                <div className={`text-xs text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                    <p className="font-semibold mb-1">NAS 설계자산 RAG 탐색</p>
                                                    <p className="text-[10px]">도면, 시방서, 회의록, 견적서를<br/>자연어로 검색할 수 있습니다</p>
                                                </div>
                                                <div className="flex flex-wrap gap-1.5 justify-center mt-1">
                                                    {['웅촌초 DWG 목록', '벽체 마감재 현황', '도서관 면적 비교', '견적 변경 이력'].map(q => (
                                                        <button key={q} onClick={() => { setRagInput(q); }}
                                                            className={`px-2 py-1 rounded-lg text-[10px] font-medium transition cursor-pointer ${isDark
                                                                ? 'bg-violet-500/10 border border-violet-500/20 text-violet-300 hover:bg-violet-500/20'
                                                                : 'bg-violet-50 border border-violet-200 text-violet-600 hover:bg-violet-100'}`}>
                                                            {q}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {ragMessages.map(msg => (
                                            <motion.div key={msg.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[90%] rounded-xl px-3 py-2.5 text-[11px] leading-relaxed ${
                                                    msg.role === 'user'
                                                        ? isDark ? 'bg-violet-500/20 text-violet-200 border border-violet-500/20' : 'bg-violet-50 text-violet-800 border border-violet-200'
                                                        : isDark ? 'bg-white/[0.04] text-gray-300 border border-white/5' : 'bg-gray-50 text-gray-700 border border-gray-200'
                                                }`}>
                                                    {msg.role === 'bot' && (
                                                        <div className="flex items-center gap-1 mb-1">
                                                            <Sparkles className="w-3 h-3 text-violet-400" />
                                                            <span className="text-[10px] font-bold text-violet-400">Poby AI</span>
                                                        </div>
                                                    )}
                                                    <div className="whitespace-pre-wrap">{msg.content || (
                                                        <span className="flex items-center gap-1 text-gray-500">
                                                            <RefreshCw className="w-3 h-3 animate-spin" /> 응답 생성 중...
                                                        </span>
                                                    )}</div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    {/* RAG Input */}
                                    <div className={`px-3 py-3 border-t shrink-0 ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-100 bg-gray-50'}`}>
                                        {selectedProject && (
                                            <div className={`text-[10px] mb-2 px-2 py-1 rounded-lg flex items-center gap-1 ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                                                <FolderOpen className="w-3 h-3" />
                                                컨텍스트: {selectedProject.name} {selectedFile ? `· ${selectedFile.name}` : ''}
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <input type="text" value={ragInput} onChange={e => setRagInput(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && sendRAG()}
                                                placeholder='NAS 설계자산 검색...'
                                                className={`flex-1 px-3 py-2 rounded-lg text-xs border outline-none transition ${isDark
                                                    ? 'bg-white/5 border-white/10 text-white placeholder-gray-600 focus:border-violet-500/50'
                                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-violet-400'}`}
                                            />
                                            <button onClick={sendRAG} disabled={ragLoading || !ragInput.trim()}
                                                className="px-3 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white text-[10px] font-bold hover:opacity-90 disabled:opacity-40 transition cursor-pointer">
                                                {ragLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* === ARCHITECTURE TAB === */}
                            {rightTab === 'arch' && (
                                <div className="space-y-4">
                                    <div className={`rounded-2xl border p-4 ${isDark ? 'bg-gray-900/60 border-white/10' : 'bg-white border-gray-200'}`}>
 <h4 className="text-xs font-bold mb-3"><Building2 className="w-4 h-4 inline-block" />NAS 파이프라인 아키텍처</h4>
                                        <div className="space-y-1.5">
                                            {[
 { icon: '', label: 'Synology NAS', desc: 'SSH 124.55.60.40 → /volume1/insight drive/B_DESIGN', status: nasSource === 'nas' },
 { icon: '', label: 'Poby (Mac Studio)', desc: 'WebDAV Gateway · 100.95.125.121', status: true },
 { icon: '', label: 'CAD_MCP Server', desc: '43+ MCP 도구 · ezdxf · win32com', status: true },
 { icon: '', label: 'CAD-Analyzer v2', desc: 'DWG→CXD · LLM 분석 · Room Detection', status: true },
 { icon: '', label: 'RAG Gateway', desc: 'Poby SSE · NAS SQLite · 27k+ files', status: true },
 { icon: '', label: 'CXD → SnapPrice', desc: '수량표 → 자동 공사비 예측', status: false },
                                            ].map((item, i) => (
                                                <div key={i} className="flex items-center gap-2.5">
                                                    <span className="text-sm">{item.icon}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-[10px] font-bold flex items-center gap-1.5">
                                                            {item.label}
                                                            <span className={`w-1.5 h-1.5 rounded-full ${item.status ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                                                        </div>
                                                        <div className={`text-[8px] truncate ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{item.desc}</div>
                                                    </div>
                                                    {i < 5 && <ChevronRight className={`w-3 h-3 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />}
                                                </div>
                                            ))}
                                        </div>
                                        <div className={`mt-3 pt-3 border-t text-[10px] ${isDark ? 'border-white/10 text-gray-600' : 'border-gray-200 text-gray-400'}`}>
                                            BTL 폴더 구조: 01_받은자료 → 03_DWG → 18_에이전트
                                        </div>
                                    </div>

                                    {/* Open Source Credits */}
                                    <div className={`rounded-xl border p-3 ${isDark ? 'bg-gray-900/40 border-white/10' : 'bg-white border-gray-200'}`}>
 <h5 className="text-[10px] font-bold mb-2"><Link className="w-4 h-4 inline-block" />Powered by</h5>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            {[
                                                { n: 'multiCAD-mcp', d: 'Multi-CAD · Batch' },
                                                { n: 'CAD-MCP', d: 'NLP · Color' },
                                                { n: 'freecad-mcp', d: 'Screenshot' },
                                                { n: 'cadquery-mcp', d: 'STL/STEP' },
                                            ].map(s => (
                                                <div key={s.n} className={`px-2 py-1.5 rounded-lg text-[10px] ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                                                    <div className="font-bold">{s.n}</div>
                                                    <div className={isDark ? 'text-gray-600' : 'text-gray-400'}>{s.d}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </main>
            
            {/* CAD Viewer Modal (Multi-tab) */}
            {openTabs.length > 0 && activeTabIndex >= 0 && (
                <div className="fixed inset-0 z-50 flex flex-col">
                    {/* Tab bar */}
                    <div className={`flex items-center gap-0.5 px-2 py-1 ${isDark ? 'bg-gray-900 border-b border-white/10' : 'bg-gray-100 border-b border-gray-200'}`}>
                        {openTabs.map((tab, idx) => (
                            <div key={tab.path} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-[11px] font-medium cursor-pointer transition ${idx === activeTabIndex
                                ? isDark ? 'bg-gray-950 text-emerald-400 border-t border-x border-white/10' : 'bg-white text-emerald-600 border-t border-x border-gray-200'
                                : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                            }`} onClick={() => setActiveTabIndex(idx)}>
                                <Component className="w-3 h-3" />
                                <span className="max-w-[120px] truncate">{tab.name}</span>
                                <button onClick={(e) => { e.stopPropagation(); const next = openTabs.filter((_, i) => i !== idx); setOpenTabs(next); setActiveTabIndex(prev => { if (next.length === 0) return -1; if (prev >= next.length) return next.length - 1; return prev >= idx ? Math.max(0, prev - 1) : prev; }); }} className="ml-1 opacity-40 hover:opacity-100 hover:text-red-400">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                        {/* Close all */}
                        {openTabs.length > 1 && (
                            <button onClick={() => { setOpenTabs([]); setActiveTabIndex(-1); }} className={`ml-auto px-2 py-1 text-[10px] rounded ${isDark ? 'text-gray-600 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}>
                                모두 닫기
                            </button>
                        )}
                    </div>
                    {/* Active viewer */}
                    {openTabs[activeTabIndex] && (
                        <CadViewer
                            file={openTabs[activeTabIndex]}
                            onClose={() => {
                                const next = openTabs.filter((_, i) => i !== activeTabIndex);
                                setOpenTabs(next);
                                setActiveTabIndex(next.length === 0 ? -1 : Math.min(activeTabIndex, next.length - 1));
                            }}
                            isDark={isDark}
                            analysisResult={analysisResult?.parsed ? { quantities: analysisResult.parsed.quantities } : null}
                        />
                    )}
                </div>
            )}
        </>
    );
}
